import { useState, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useReadingContext } from '../../contexts/ReadingContext';
import { Link } from 'react-router-dom';
import { ChevronLeft, Plus, Lightbulb, ExternalLink, Heart, Clock, X, Edit3, Trash2, PlusCircle, BookOpen, Wrench, Star, Brain, Users, User, Calendar, Eye, ChefHat, MessageSquare, Film, BarChart3, Sparkles, Settings } from 'lucide-react';
import IdeaForm from './IdeaForm';
import SmartIdeaCreationModal from './SmartIdeaCreationModal';
import EnhancedRecommendationCard from './EnhancedRecommendationCard';
import EnhancedRecommendationModal from './EnhancedRecommendationModal';
import EnhancedBookModal from './EnhancedBookModal';
import { MobileEngagementModal } from './MobileEngagementInterface';
import ConvertToActivityModal from './ConvertToActivityModal';
import ProjectSetupFlow from '../Projects/ProjectSetupFlow';
import ActiveProjectWorkspace from '../Projects/ActiveProjectWorkspace';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import QuickRatingInterface from './QuickRatingInterface';
import { readingService } from '../../lib/reading-service';
import { tasksService } from '../../lib/tasks-service';
import { toast } from 'react-hot-toast';

export default function RecsIdeas() {
  const { currentUser, recsIdeas, updateIdeaStatus, deleteIdea, family, refreshFamilyData } = useFamilyContext();
  const { addToReadingList, startNewBook } = useReadingContext();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const useEnhancedInterface = true; // Enhanced interface is now the default

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPerson, setSelectedPerson] = useState('all');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedCuisine, setSelectedCuisine] = useState('all');
  const [showAudiobooksOnly, setShowAudiobooksOnly] = useState(false);
  const [convertingIdea, setConvertingIdea] = useState(null);
  const [selectedIdeaDetails, setSelectedIdeaDetails] = useState(null);
  const [engagingWithIdea, setEngagingWithIdea] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [currentView, setCurrentView] = useState('personal'); // 'personal' or 'family'
  
  // Project setup and workspace states
  const [projectSetupIdea, setProjectSetupIdea] = useState(null);
  const [activeProjectConfig, setActiveProjectConfig] = useState(null);

  // Watch tracking states (for movie cards that might still exist)
  const [watchedMovies, setWatchedMovies] = useState(new Map()); // movieId → watch data
  const [loadingWatchHistory, setLoadingWatchHistory] = useState(false);

  const isActualParent = currentUser?.isParent && !currentUser?.originalParent;
  const isParent = currentUser?.isParent || currentUser?.originalParent;
  
  // Get ideas based on current view mode
  const relevantIdeas = (recsIdeas || []).filter(idea => {
    if (currentView === 'family') {
      return true; // Show all family ideas
    } else {
      // Personal view - show only ideas for current user
      if (isActualParent) return true; // Parents see all in personal view too
      return idea.targetKids && Array.isArray(idea.targetKids) && idea.targetKids.includes(currentUser.id);
    }
  });

  // Calculate stats for the library overview
  const getLibraryStats = () => {
    const userIdeas = currentView === 'personal' ? relevantIdeas.filter(idea => {
      if (isActualParent) return true;
      return idea.targetKids && Array.isArray(idea.targetKids) && idea.targetKids.includes(currentUser.id);
    }) : relevantIdeas;

    const newForYou = userIdeas.filter(idea => {
      if (isActualParent) return false; // Parents don't have response status
      const status = idea.kidResponses?.[currentUser.id]?.status;
      return !status || status === 'new';
    }).length;

    const inProgress = userIdeas.filter(idea => {
      if (isActualParent) return false;
      const status = idea.kidResponses?.[currentUser.id]?.status;
      return status === 'interested' || status === 'maybe';
    }).length;

    const completed = userIdeas.filter(idea => {
      if (isActualParent) return false;
      const status = idea.kidResponses?.[currentUser.id]?.status;
      return status === 'completed' || status === 'done';
    }).length;

    return { newForYou, inProgress, completed, total: userIdeas.length };
  };

  const stats = getLibraryStats();

  // Calculate ideas after category filters (for accurate count display)
  const ideasAfterCategoryFilters = relevantIdeas.filter(idea => {
    // Exclude movies - they're in the Watch Library now
    if (idea.category === 'movie') return false;

    const categoryMatch = selectedCategory === 'all' || idea.category === selectedCategory;

    let personMatch = true;
    if (selectedPerson !== 'all') {
      if (selectedPerson === 'everyone') {
        personMatch = !idea.targetKids || idea.targetKids.length === 0 ||
                     (idea.targetKids && idea.targetKids.includes(currentUser.id));
      } else {
        personMatch = idea.targetKids && idea.targetKids.includes(selectedPerson);
      }
    }

    let genreMatch = true;
    if (selectedGenre !== 'all' && idea.category === 'book') {
      genreMatch = idea.genre === selectedGenre;
    }

    let cuisineMatch = true;
    if (selectedCuisine !== 'all' && idea.category === 'recipe') {
      cuisineMatch = idea.cuisine === selectedCuisine;
    }

    let audiobookMatch = true;
    if (showAudiobooksOnly && idea.category === 'book') {
      audiobookMatch = idea.isAudiobook === true;
    }

    return categoryMatch && personMatch && genreMatch && cuisineMatch && audiobookMatch;
  });

  // Final filtered ideas (including watch filter for movies)
  const filteredIdeas = ideasAfterCategoryFilters.filter(idea => {
    // Watch filter (for movies)
    if (idea.category === 'movie') {
      const isWatched = watchedMovies.has(idea.id);
      if (watchFilter === 'to-watch') {
        return !isWatched;
      } else if (watchFilter === 'watched') {
        return isWatched;
      }
    }
    return true; // Show all non-movie items
  });

  const categories = ['all', 'book', 'project', 'experience', 'learning', 'recipe'];

  // Fetch watch history (extracted as standalone function for reuse)
  const fetchWatchHistory = async () => {
    if (!currentUser?.id) return;

    setLoadingWatchHistory(true);
    try {
      const response = await fetch('/api/watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get-watch-history',
          userId: currentUser.id
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      if (data.success && data.history) {
        // Convert array to Map for quick lookups
        const watchMap = new Map();
        data.history.forEach(entry => {
          if (entry.entertainment_id) {
            watchMap.set(entry.entertainment_id, {
              rating: entry.watch_rating,
              watchedDate: entry.watched_date,
              status: entry.watch_status,
              notes: entry.notes
            });
          }
        });
        setWatchedMovies(watchMap);
      }
    } catch (error) {
      console.error('Error fetching watch history:', error);
      toast.error('Failed to load watch history');
    } finally {
      setLoadingWatchHistory(false);
    }
  };

  // Fetch watch history on mount
  useEffect(() => {
    fetchWatchHistory();
  }, [currentUser?.id]);

  // Handle watch rating updates
  const handleWatchUpdate = async (historyEntry, movieId) => {
    // If historyEntry is null, it means we're marking as unseen (removing the entry)
    if (historyEntry === null && movieId) {
      setWatchedMovies(prev => {
        const newMap = new Map(prev);
        newMap.delete(movieId);
        return newMap;
      });
      return;
    }

    if (!historyEntry || !historyEntry.entertainment_id) return;

    // Update local state immediately
    setWatchedMovies(prev => {
      const newMap = new Map(prev);
      newMap.set(historyEntry.entertainment_id, {
        rating: historyEntry.watch_rating,
        watchedDate: historyEntry.watched_date,
        status: historyEntry.watch_status,
        notes: historyEntry.notes
      });
      return newMap;
    });

    // Show success toast
    const ratingLabels = {
      loved: 'Loved ❤️',
      liked: 'Liked 👍',
      neutral: 'Meh 😐',
      hated: 'Disliked 👎',
      disliked: 'Disliked 👎'
    };
    toast.success(`Marked as ${ratingLabels[historyEntry.watch_rating] || 'Seen'}`);
  };

  // Handle deleting idea (removes from both ideas and family_watch_history tables)
  const handleDeleteIdea = async (ideaId) => {
    try {
      // Delete from both tables via FamilyContext
      await deleteIdea(ideaId);

      // Also remove from local watchedMovies state if it was there
      setWatchedMovies(prev => {
        const newMap = new Map(prev);
        newMap.delete(ideaId);
        return newMap;
      });

      toast.success('Deleted from library and watch history');
    } catch (error) {
      console.error('Error deleting idea:', error);
      toast.error('Failed to delete');
    }
  };

  const handleIdeaResponse = async (ideaId, status) => {
    if (!isActualParent) {
      // Handle special case for adding to Future Big3
      if (status === 'add_to_big3') {
        const idea = recsIdeas.find(i => i.id === ideaId);
        if (idea && idea.category !== 'movie') {
          try {
            // Create a task marked for Big3
            const taskData = {
              ownerId: currentUser.id,
              title: idea.title,
              description: `${idea.description}\n\n📝 From Recommendations: ${idea.category}`,
              category: 'selected_for_big3',
              taskType: 'recommendation',
              priority: 'medium',
              createdBy: currentUser.id
            };
            
            await tasksService.createTask(taskData);
            
            // Update idea status to show it's been added
            updateIdeaStatus(ideaId, currentUser.id, 'added_to_big3');
            
            toast.success('Added to your tasks for Future Big3!', {
              icon: '🎯',
              duration: 3000
            });
          } catch (error) {
            console.error('Error adding to Big3 tasks:', error);
            toast.error('Failed to add to Big3 tasks');
          }
        }
      } else if (status === 'reading_challenge') {
        const idea = recsIdeas.find(i => i.id === ideaId);
        if (idea && idea.category === 'book') {
          try {
            // Check if user has an active reading challenge
            const activeChallenge = await readingService.getActiveChallenge(currentUser.id);
            
            if (activeChallenge) {
              // Add book to reading list first
              const bookData = {
                title: idea.title,
                author: idea.author,
                totalPages: idea.totalPages || 200, // Default if not specified
                isbn: idea.isbn,
                description: idea.description,
                genre: idea.genre
              };
              
              await addToReadingList(bookData);
              
              // Update idea status to show it's been added to challenge
              updateIdeaStatus(ideaId, currentUser.id, 'reading_list');
              
              toast.success('Book added to your Reading Challenge!', {
                icon: '🎯',
                duration: 3000
              });
            } else {
              // No active challenge, prompt to create one
              toast.error('No active reading challenge found. Please create one first!', {
                icon: '📚',
                duration: 4000
              });
            }
          } catch (error) {
            console.error('Error adding book to reading challenge:', error);
            toast.error('Failed to add book to reading challenge');
          }
        }
      } else {
        // Handle all other response types normally
        updateIdeaStatus(ideaId, currentUser.id, status);
      }
    }
  };

  const getIdeaStatus = (idea) => {
    if (isActualParent) return null;
    return idea.kidResponses?.[currentUser.id]?.status || 'new';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'book': return <BookOpen className="w-4 h-4" />;
      case 'project': return <Wrench className="w-4 h-4" />;
      case 'experience': return <Star className="w-4 h-4" />;
      case 'learning': return <Brain className="w-4 h-4" />;
      case 'recipe': return <ChefHat className="w-4 h-4" />;
      case 'movie': return <Film className="w-4 h-4" />;
      default: return <Lightbulb className="w-4 h-4" />;
    }
  };

  const getUserById = (userId) => {
    // Combine parents and kids to create familyMembers array
    const allMembers = [
      ...(family?.parents || []),
      ...(family?.kids || [])
    ];
    return allMembers.find(member => member.id === userId);
  };

  const getUserNameById = (userId) => {
    const member = getUserById(userId);
    return member?.name || 'Unknown User';
  };

  // Project setup handlers
  const handleStartProject = (projectIdea) => {
    setProjectSetupIdea(projectIdea);
  };

  const handleProjectSetupComplete = (projectConfig) => {
    setProjectSetupIdea(null);
    setActiveProjectConfig(projectConfig);
  };

  const handleSaveProject = (projectData) => {
    // Here you would save to your backend/database
    console.log('Saving project:', projectData);
    // For now, just close the workspace
    setActiveProjectConfig(null);
  };



  const getTargetKidsDetails = (targetKids) => {
    if (!targetKids || !Array.isArray(targetKids) || targetKids.length === 0) {
      return [];
    }
    return targetKids.map(kidId => getUserById(kidId)).filter(Boolean);
  };

  return (
    <div className="bg-stone-50 min-h-screen">
      {/* Library Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 md:py-5">
            <div className="flex items-center gap-2 md:gap-4">
              <Link 
                to="/dashboard" 
                className="p-1.5 md:p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-lg md:text-xl font-bold text-stone-900">Recommendations Library</h1>
                <p className="text-stone-600 text-xs md:text-base hidden sm:block">
                  {currentView === 'personal' ? 'Curated recommendations just for you' : 'All family recommendations and ideas'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3">
              {isActualParent && (
                <>
                  <Link
                    to="/recs-ideas/responses"
                    className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 bg-azure-500 text-white text-xs md:text-sm font-medium rounded-xl hover:bg-azure-600 transition-colors min-h-[44px]"
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span className="hidden sm:inline">Responses</span>
                  </Link>
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 bg-teal-600 text-white text-xs md:text-sm font-medium rounded-xl hover:bg-teal-700 transition-colors min-h-[44px]"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Add Idea</span>
                    <span className="sm:hidden">Add</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Library Overview Section */}
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-6 md:p-8 my-6 md:my-8 text-white relative overflow-hidden">
          {/* Decorative Background Circle */}
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-white bg-opacity-10 rounded-full"></div>
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                {currentView === 'personal' ? 'Your Personal Library' : 'Family Library'}
              </h2>
              <p className="text-teal-100 mb-4 text-sm md:text-base">
                {currentView === 'personal' 
                  ? 'Curated recommendations just for you' 
                  : 'All family recommendations and ideas'}
              </p>
              <div className="flex gap-4 md:gap-6">
                <div>
                  <div className="text-xl md:text-2xl font-bold">{stats.newForYou}</div>
                  <div className="text-xs md:text-sm text-teal-100">New for you</div>
                </div>
                <div>
                  <div className="text-xl md:text-2xl font-bold">{stats.inProgress}</div>
                  <div className="text-xs md:text-sm text-teal-100">In progress</div>
                </div>
                <div>
                  <div className="text-xl md:text-2xl font-bold">{stats.completed}</div>
                  <div className="text-xs md:text-sm text-teal-100">Completed</div>
                </div>
              </div>
            </div>
            
            {/* View Toggle */}
            <div className="flex justify-start lg:justify-end mt-4 lg:mt-0">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-1.5 md:p-2 flex gap-1">
                <button
                  onClick={() => setCurrentView('personal')}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all min-h-[36px] md:min-h-[40px] ${
                    currentView === 'personal'
                      ? 'bg-white bg-opacity-25 text-white'
                      : 'text-teal-100 hover:text-white hover:bg-white hover:bg-opacity-10'
                  }`}
                >
                  For You
                </button>
                <button
                  onClick={() => setCurrentView('family')}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all min-h-[36px] md:min-h-[40px] ${
                    currentView === 'family'
                      ? 'bg-white bg-opacity-25 text-white'
                      : 'text-teal-100 hover:text-white hover:bg-white hover:bg-opacity-10'
                  }`}
                >
                  Family
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Category Filters - Library Style */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-stone-700">Browse by Category</h3>
            <button
              onClick={() => setSelectedCategory('all')}
              className="text-xs text-stone-500 hover:text-stone-700 transition-colors"
            >
              Clear all
            </button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
            {[
              { key: 'all', label: 'All Ideas', icon: '🌟' },
              { key: 'book', label: 'Books', icon: '📚' },
              { key: 'project', label: 'Projects', icon: '🛠️' },
              { key: 'learning', label: 'Learning', icon: '🎓' },
              { key: 'experience', label: 'Experiences', icon: '✨' },
              { key: 'recipe', label: 'Recipes', icon: '🔍' },
              { key: 'movie', label: 'Movies', icon: '🎬' }
            ].map(category => {
              const count = relevantIdeas.filter(idea =>
                category.key === 'all' || idea.category === category.key
              ).length;

              return (
                <button
                  key={category.key}
                  onClick={() => setSelectedCategory(category.key)}
                  className={`relative border rounded-lg p-3 text-center transition-all ${
                    selectedCategory === category.key
                      ? 'border-teal-500 bg-teal-50 shadow-sm'
                      : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                  }`}
                >
                  {selectedCategory === category.key && (
                    <div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-teal-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-[10px] font-bold">✓</span>
                    </div>
                  )}
                  <div className="text-2xl mb-1.5">{category.icon}</div>
                  <div className="font-medium text-xs text-stone-900 mb-0.5">{category.label}</div>
                  <div className="text-[11px] text-stone-500">{count} items</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Watch Library Link */}
        <div className="mb-4 md:mb-6">
          <Link to="/recs-ideas/watch">
            <div className="border-2 border-violet-300 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl p-4 hover:shadow-lg transition-all">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-violet-600 rounded-lg flex items-center justify-center">
                    <Film className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-stone-900">Watch Library</h3>
                    <p className="text-sm text-stone-600">Track movies & TV shows you want to watch</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right hidden sm:block">
                    <div className="text-2xl font-bold text-violet-600">
                      {relevantIdeas.filter(idea => idea.category === 'movie').length}
                    </div>
                    <div className="text-xs text-stone-500">items</div>
                  </div>
                  <ChevronLeft className="w-6 h-6 text-violet-600 transform rotate-180" />
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Person Filter */}
        <div className="mb-4 md:mb-6">
          <div className="text-sm font-medium text-stone-700 mb-3">Filter by person:</div>
          <div className="flex flex-wrap gap-2">
            {/* All filter */}
            <button
              onClick={() => setSelectedPerson('all')}
              className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors min-h-[44px] ${
                selectedPerson === 'all' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              All People
            </button>

            {/* For Me filter (general recommendations + ones targeted to current user) */}
            <button
              onClick={() => setSelectedPerson('everyone')}
              className={`flex items-center gap-1 md:gap-1.5 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors min-h-[44px] ${
                selectedPerson === 'everyone' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              <Users className="w-4 h-4" />
              For Me
            </button>

            {/* Individual family members */}
            {[...(family?.parents || []), ...(family?.kids || [])].map(member => {
              const count = relevantIdeas.filter(idea => 
                idea.targetKids && idea.targetKids.includes(member.id)
              ).length;
              
              return (
                <button
                  key={member.id}
                  onClick={() => setSelectedPerson(member.id)}
                  className={`flex items-center gap-1 md:gap-1.5 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors min-h-[44px] ${
                    selectedPerson === member.id 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  <AvatarOrInitial
                    avatar={member.avatar}
                    name={member.name}
                    size="xs"
                  />
                  <span className="hidden sm:inline">{member.name}</span>
                  <span className="sm:hidden">{member.name.split(' ')[0]}</span>
                  <span className="ml-1 text-xs opacity-75">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Category-specific filters */}
        {selectedCategory === 'book' && (
          <div className="mb-4">
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
              {/* Genre filter */}
              <div className="flex-1 min-w-[200px]">
                <div className="text-sm font-medium text-stone-700 mb-2">Filter by genre:</div>
                <select
                  value={selectedGenre}
                  onChange={(e) => setSelectedGenre(e.target.value)}
                  className="w-full px-3 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                >
                  <option value="all">All Genres</option>
                  {[...new Set(relevantIdeas
                    .filter(idea => idea.category === 'book' && idea.genre)
                    .map(idea => idea.genre)
                  )].map(genre => (
                    <option key={genre} value={genre}>{genre}</option>
                  ))}
                </select>
              </div>

              {/* Audiobook toggle */}
              <div className="flex items-end">
                <label className="flex items-center min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={showAudiobooksOnly}
                    onChange={(e) => setShowAudiobooksOnly(e.target.checked)}
                    className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-stone-300 rounded"
                  />
                  <span className="ml-2 text-sm md:text-base text-stone-700">Audiobooks only</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {selectedCategory === 'recipe' && (
          <div className="mb-4">
            <div className="text-sm font-medium text-stone-700 mb-2">Filter by cuisine:</div>
            <select
              value={selectedCuisine}
              onChange={(e) => setSelectedCuisine(e.target.value)}
              className="w-full max-w-sm px-3 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
            >
              <option value="all">All Cuisines</option>
              {[...new Set(relevantIdeas
                .filter(idea => idea.category === 'recipe' && idea.cuisine)
                .map(idea => idea.cuisine)
              )].map(cuisine => (
                <option key={cuisine} value={cuisine}>{cuisine}</option>
              ))}
            </select>
          </div>
        )}

        {/* Results Section */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6 gap-4">
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-stone-900">
                {currentView === 'personal' ? 'Recommendations for You' : 'All Family Recommendations'}
              </h3>
              <p className="text-sm text-stone-500">
                Showing {filteredIdeas.length} of {ideasAfterCategoryFilters.length} items
              </p>
            </div>
            <div className="flex gap-2 overflow-x-auto">
              <button className="px-3 py-2 text-xs bg-stone-100 border border-stone-200 rounded-lg hover:bg-stone-200 transition-colors min-h-[36px] whitespace-nowrap">
                Recent
              </button>
              <button className="px-3 py-2 text-xs bg-white border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors min-h-[36px] whitespace-nowrap">
                Popular
              </button>
              <button className="px-3 py-2 text-xs bg-white border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors min-h-[36px] whitespace-nowrap">
                Category
              </button>
            </div>
          </div>

          {/* Ideas Grid */}
          {filteredIdeas.length === 0 ? (
            <div className="text-center py-12 md:py-16">
              <div className="text-4xl md:text-6xl mb-4">📚</div>
              <h3 className="text-base md:text-lg font-semibold text-stone-900 mb-2">No recommendations found</h3>
              <p className="text-stone-600 mb-6 text-sm md:text-base">
                {isParent 
                  ? "Try adjusting your filters or add a new recommendation"
                  : "Tell your parents you need some new ideas!"
                }
              </p>
              {isActualParent && (
                <button 
                  onClick={() => setShowAddForm(true)} 
                  className="flex items-center gap-2 mx-auto px-6 py-3 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 transition-colors min-h-[44px] text-sm md:text-base"
                >
                  <Plus className="w-5 h-5" />
                  Add First Idea
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredIdeas.map(idea => {
              const ideaStatus = getIdeaStatus(idea);
              const createdBy = getUserById(idea.createdBy || idea.created_by);
              const targetKids = getTargetKidsDetails(idea.targetKids);
              
              // Enhanced card for new interface
              if (useEnhancedInterface) {
                return (
                  <EnhancedRecommendationCard
                    key={idea.id}
                    recommendation={{
                      ...idea,
                      authorName: createdBy?.name || 'Unknown User',
                      authorAvatar: createdBy?.avatar,
                      targetKids: targetKids.map(kid => ({
                        id: kid.id,
                        name: kid.name,
                        avatar: kid.avatar
                      }))
                    }}
                    onViewDetails={setSelectedIdeaDetails}
                    onEdit={setEditingIdea}
                    onDelete={handleDeleteIdea}
                    onConfirmDelete={setDeleteConfirmId}
                    onResponse={(ideaId, responseType) => {
                      if (!isParent) {
                        setEngagingWithIdea(ideaId);
                      }
                    }}
                    onStartProject={handleStartProject}
                    currentUser={currentUser}
                    isParent={isActualParent}
                    onWatchUpdate={handleWatchUpdate}
                    watchedMovies={watchedMovies}
                  />
                );
              }
              
              // Legacy card (fallback)
              return (
                <div 
                  key={idea.id} 
                  className="bg-white rounded-xl border border-stone-200 hover:border-stone-300 hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden"
                  onClick={() => setSelectedIdeaDetails(idea)}
                >
                  {/* Card Header */}
                  <div className="p-3 md:p-4 border-b border-stone-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-lg ${
                          idea.category === 'book' ? 'bg-emerald-100 text-emerald-600' :
                          idea.category === 'project' ? 'bg-orange-100 text-orange-600' :
                          idea.category === 'experience' ? 'bg-violet-100 text-violet-600' :
                          idea.category === 'learning' ? 'bg-azure-100 text-azure-600' :
                          idea.category === 'recipe' ? 'bg-red-100 text-red-600' :
                          idea.category === 'movie' ? 'bg-indigo-100 text-indigo-600' :
                          'bg-yellow-100 text-yellow-600'
                        }`}>
                          {getCategoryIcon(idea.category)}
                        </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          idea.category === 'book' ? 'bg-emerald-50 text-emerald-700' :
                          idea.category === 'project' ? 'bg-orange-50 text-orange-700' :
                          idea.category === 'experience' ? 'bg-violet-50 text-violet-700' :
                          idea.category === 'learning' ? 'bg-azure-50 text-azure-700' :
                          idea.category === 'recipe' ? 'bg-red-50 text-red-700' :
                          idea.category === 'movie' ? 'bg-indigo-50 text-indigo-700' :
                          'bg-yellow-50 text-yellow-700'
                        }`}>
                          {idea.category}
                        </span>
                      </div>
                      
                      {isActualParent && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingIdea(idea);
                            }}
                            className="p-2 md:p-1.5 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors min-h-[44px] md:min-h-auto flex items-center justify-center"
                            title="Edit idea"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirmId(idea.id);
                            }}
                            className="p-2 md:p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors min-h-[44px] md:min-h-auto flex items-center justify-center"
                            title="Delete idea"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Who Added & Who It's For */}
                    <div className="flex items-center justify-between text-xs text-stone-600">
                      <div className="flex items-center gap-1.5">
                        <AvatarOrInitial
                          avatar={createdBy?.avatar}
                          name={createdBy?.name}
                          size="sm"
                        />
                        <span>by {createdBy?.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {targetKids.length === 0 ? (
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            <span>Everyone</span>
                          </div>
                        ) : targetKids.length === 1 ? (
                          <div className="flex items-center gap-1">
                            <AvatarOrInitial
                              avatar={targetKids[0]?.avatar}
                              name={targetKids[0]?.name}
                              size="xs"
                            />
                            <span>{targetKids[0]?.name}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <div className="flex -space-x-1">
                              {targetKids.map((kid) => (
                                <AvatarOrInitial
                                  key={kid.id}
                                  avatar={kid?.avatar}
                                  name={kid?.name}
                                  size="xs"
                                  className="ring-1 ring-white"
                                  title={kid?.name}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-3 md:p-4">
                    <h3 className="font-semibold text-stone-900 mb-2 overflow-hidden" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}>{idea.title}</h3>
                    <p className="text-sm text-stone-600 mb-3 overflow-hidden" style={{display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical'}}>{idea.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-stone-400">
                        {new Date(idea.created_at).toLocaleDateString()}
                      </span>
                      <button className="flex items-center gap-1 text-xs md:text-sm text-azure-600 hover:text-azure-700 font-medium min-h-[36px] md:min-h-auto">
                        <Eye className="w-3 h-3" />
                        View Details
                      </button>
                    </div>
                  </div>

                  {/* Kid Response Actions */}
                  {!isParent && (
                    <div className="p-3 md:p-4 border-t border-stone-100 bg-stone-50">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleIdeaResponse(idea.id, 'interested');
                          }}
                          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors min-h-[44px] ${
                            ideaStatus === 'interested' 
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                              : 'bg-white text-stone-600 border border-stone-200 hover:border-emerald-300'
                          }`}
                        >
                          <Heart className="w-3 h-3" />
                          Interested
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleIdeaResponse(idea.id, 'maybe');
                          }}
                          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors min-h-[44px] ${
                            ideaStatus === 'maybe' 
                              ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' 
                              : 'bg-white text-stone-600 border border-stone-200 hover:border-yellow-300'
                          }`}
                        >
                          <Clock className="w-3 h-3" />
                          Maybe
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleIdeaResponse(idea.id, 'not-for-me');
                          }}
                          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors min-h-[44px] ${
                            ideaStatus === 'not-for-me' 
                              ? 'bg-red-100 text-red-700 border border-red-200' 
                              : 'bg-white text-stone-600 border border-stone-200 hover:border-red-300'
                          }`}
                        >
                          <X className="w-3 h-3" />
                          Not for Me
                        </button>
                      </div>
                      
                      {ideaStatus === 'interested' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConvertingIdea(idea);
                          }}
                          className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-3 bg-azure-600 text-white text-sm font-medium rounded-lg hover:bg-azure-700 transition-colors min-h-[44px]"
                        >
                          <PlusCircle className="w-4 h-4" />
                          Convert to Activity
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          )}
        </div>
      </main>



      {/* Enhanced Creation Modal */}
      {showAddForm && useEnhancedInterface && (
        <SmartIdeaCreationModal onClose={() => setShowAddForm(false)} />
      )}
      
      {/* Legacy Form Modal (fallback) */}
      {showAddForm && !useEnhancedInterface && (
        <IdeaForm onClose={() => setShowAddForm(false)} />
      )}

      {/* Enhanced Edit Modal */}
      {editingIdea && useEnhancedInterface && (
        <SmartIdeaCreationModal
          editingIdea={editingIdea}
          onClose={() => setEditingIdea(null)}
        />
      )}
      
      {/* Legacy Edit Modal (fallback) */}
      {editingIdea && !useEnhancedInterface && (
        <IdeaForm
          idea={editingIdea}
          onClose={() => setEditingIdea(null)}
        />
      )}

      {/* Convert to Activity Modal */}
      {convertingIdea && (
        <ConvertToActivityModal
          idea={convertingIdea}
          kidId={currentUser?.id}
          onClose={() => setConvertingIdea(null)}
        />
      )}

      {/* Mobile Engagement Interface */}
      {engagingWithIdea && (
        <MobileEngagementModal
          recommendationId={engagingWithIdea}
          onClose={() => setEngagingWithIdea(null)}
          currentUser={currentUser}
        />
      )}

      {/* Enhanced Details Modal - Use special book modal for books */}
      {selectedIdeaDetails && useEnhancedInterface && selectedIdeaDetails.category === 'book' && (
        <EnhancedBookModal
          book={{
            ...selectedIdeaDetails,
            authorName: getUserNameById(selectedIdeaDetails.createdBy || selectedIdeaDetails.created_by),
            whyRecommended: selectedIdeaDetails.whyRecommended || selectedIdeaDetails.why_recommended,
            personalNote: selectedIdeaDetails.personalNote, // Include personal note field
            targetKids: getTargetKidsDetails(selectedIdeaDetails.targetKids),
            // Ensure book fields fall back to linkMetadata if direct fields are empty
            author: selectedIdeaDetails.author || selectedIdeaDetails.link_metadata?.author,
            genre: selectedIdeaDetails.genre || selectedIdeaDetails.link_metadata?.genre,
            isAudiobook: selectedIdeaDetails.is_audiobook || selectedIdeaDetails.link_metadata?.isAudiobook || selectedIdeaDetails.link_metadata?.audiobook,
            isbn: selectedIdeaDetails.isbn || selectedIdeaDetails.link_metadata?.isbn,
            releaseYear: selectedIdeaDetails.release_year || selectedIdeaDetails.link_metadata?.releaseYear
          }}
          onClose={() => setSelectedIdeaDetails(null)}
          onEdit={(idea) => {
            setEditingIdea(idea);
            setSelectedIdeaDetails(null); // Close detail modal when opening edit modal
          }}
          onDelete={(idea) => {
            handleDeleteIdea(idea.id);
            setSelectedIdeaDetails(null);
          }}
          onConfirmDelete={(ideaId) => {
            setDeleteConfirmId(ideaId);
            setSelectedIdeaDetails(null);
          }}
          onResponse={(ideaId, responseType) => {
            if (!isParent) {
              handleIdeaResponse(ideaId, responseType);
              setSelectedIdeaDetails(null);
            }
          }}
          isParent={isActualParent}
        />
      )}

      {/* Enhanced Details Modal - Regular modal for non-books */}
      {selectedIdeaDetails && useEnhancedInterface && selectedIdeaDetails.category !== 'book' && (
        <EnhancedRecommendationModal
          recommendation={{
            ...selectedIdeaDetails,
            authorName: getUserNameById(selectedIdeaDetails.createdBy || selectedIdeaDetails.created_by),
            targetKids: getTargetKidsDetails(selectedIdeaDetails.targetKids)
          }}
          onClose={() => setSelectedIdeaDetails(null)}
          onEdit={(idea) => {
            setEditingIdea(idea);
            setSelectedIdeaDetails(null); // Close detail modal when opening edit modal
          }}
          onDelete={(idea) => {
            handleDeleteIdea(idea.id);
            setSelectedIdeaDetails(null);
          }}
          onConfirmDelete={(ideaId) => {
            setDeleteConfirmId(ideaId);
            setSelectedIdeaDetails(null);
          }}
          onResponse={(ideaId, responseType) => {
            if (!isParent) {
              setEngagingWithIdea(ideaId);
              setSelectedIdeaDetails(null);
            }
          }}
          onStartProject={(projectIdea) => {
            handleStartProject(projectIdea);
            setSelectedIdeaDetails(null);
          }}
          onConvert={setConvertingIdea}
          currentUser={currentUser}
          isParent={isActualParent}
          family={family}
        />
      )}
      
      {/* Legacy Details Modal (fallback) */}
      {selectedIdeaDetails && !useEnhancedInterface && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-stone-200 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-xl ${
                    selectedIdeaDetails.category === 'book' ? 'bg-emerald-100 text-emerald-600' :
                    selectedIdeaDetails.category === 'project' ? 'bg-orange-100 text-orange-600' :
                    selectedIdeaDetails.category === 'experience' ? 'bg-violet-100 text-violet-600' :
                    selectedIdeaDetails.category === 'learning' ? 'bg-azure-100 text-azure-600' :
                    selectedIdeaDetails.category === 'recipe' ? 'bg-red-100 text-red-600' :
                    selectedIdeaDetails.category === 'movie' ? 'bg-indigo-100 text-indigo-600' :
                    'bg-yellow-100 text-yellow-600'
                  }`}>
                    {getCategoryIcon(selectedIdeaDetails.category)}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-stone-900">{selectedIdeaDetails.title}</h2>
                    <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                      selectedIdeaDetails.category === 'book' ? 'bg-emerald-50 text-emerald-700' :
                      selectedIdeaDetails.category === 'project' ? 'bg-orange-50 text-orange-700' :
                      selectedIdeaDetails.category === 'experience' ? 'bg-violet-50 text-violet-700' :
                      selectedIdeaDetails.category === 'learning' ? 'bg-azure-50 text-azure-700' :
                      selectedIdeaDetails.category === 'recipe' ? 'bg-red-50 text-red-700' :
                      selectedIdeaDetails.category === 'movie' ? 'bg-indigo-50 text-indigo-700' :
                      'bg-yellow-50 text-yellow-700'
                    }`}>
                      {selectedIdeaDetails.category}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedIdeaDetails(null)}
                  className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Who Added & Who It's For Section */}
              <div className="bg-stone-50 rounded-xl p-4 mb-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Added by
                    </h4>
                    <div className="flex items-center gap-3">
                      <AvatarOrInitial
                        avatar={getUserById(selectedIdeaDetails.createdBy || selectedIdeaDetails.created_by)?.avatar}
                        name={getUserById(selectedIdeaDetails.createdBy || selectedIdeaDetails.created_by)?.name}
                        size="md"
                      />
                      <div>
                        <div className="font-medium text-stone-900">
                          {getUserNameById(selectedIdeaDetails.createdBy || selectedIdeaDetails.created_by)}
                        </div>
                        <div className="text-sm text-stone-500">
                          {new Date(selectedIdeaDetails.created_at).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Suggested for
                    </h4>
                    {getTargetKidsDetails(selectedIdeaDetails.targetKids).length === 0 ? (
                      <div className="flex items-center gap-2 text-stone-600">
                        <Users className="w-5 h-5 text-stone-400" />
                        <span>Everyone in the family</span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {getTargetKidsDetails(selectedIdeaDetails.targetKids).map(kid => (
                          <div key={kid.id} className="flex items-center gap-3">
                            <AvatarOrInitial
                              avatar={kid?.avatar}
                              name={kid?.name}
                              size="sm"
                            />
                            <span className="font-medium text-stone-900">{kid?.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-stone-700 mb-3">Description</h4>
                <div className="prose prose-stone prose-sm max-w-none">
                  <p className="text-stone-600 leading-relaxed">{selectedIdeaDetails.description}</p>
                </div>
              </div>

              {/* Links */}
              {selectedIdeaDetails.links && selectedIdeaDetails.links.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-stone-700 mb-3">Links & Resources</h4>
                  <div className="space-y-2">
                    {selectedIdeaDetails.links.map((link, index) => (
                      <a
                        key={index}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-azure-50 hover:bg-azure-100 rounded-lg transition-colors group"
                      >
                        <ExternalLink className="w-4 h-4 text-azure-600" />
                        <span className="text-azure-700 group-hover:text-azure-800 text-sm truncate">{link}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Kid Response Actions */}
              {!isParent && (
                <div className="border-t border-stone-200 pt-6">
                  <h4 className="text-sm font-medium text-stone-700 mb-3">What do you think?</h4>
                  <div className="flex flex-wrap gap-3 mb-4">
                    {[
                      { key: 'interested', label: 'Interested', icon: Heart, color: 'emerald' },
                      { key: 'maybe', label: 'Maybe Later', icon: Clock, color: 'yellow' },
                      { key: 'more-details', label: 'More Details', icon: ExternalLink, color: 'azure' },
                      { key: 'not-for-me', label: 'Not for Me', icon: X, color: 'red' }
                    ].map(({ key, label, icon: ButtonIcon, color }) => {
                      const isActive = getIdeaStatus(selectedIdeaDetails) === key;
                      return (
                        <button
                          key={key}
                          onClick={() => handleIdeaResponse(selectedIdeaDetails.id, key)}
                          className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors min-h-[44px] ${
                            isActive
                              ? `bg-${color}-100 text-${color}-700 border border-${color}-200`
                              : 'bg-white text-stone-600 border border-stone-200 hover:border-stone-300'
                          }`}
                        >
                          <ButtonIcon className="w-4 h-4" />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  
                  {getIdeaStatus(selectedIdeaDetails) === 'interested' && (
                    <button
                      onClick={() => {
                        setConvertingIdea(selectedIdeaDetails);
                        setSelectedIdeaDetails(null);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-azure-600 text-white font-medium rounded-lg hover:bg-azure-700 transition-colors min-h-[44px]"
                    >
                      <PlusCircle className="w-5 h-5" />
                      Convert to Activity
                    </button>
                  )}
                </div>
              )}

              {/* Parent Actions */}
              {isActualParent && (
                <div className="border-t border-stone-200 pt-6">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => {
                        setEditingIdea(selectedIdeaDetails);
                        setSelectedIdeaDetails(null);
                      }}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-azure-600 text-white font-medium rounded-lg hover:bg-azure-700 transition-colors min-h-[44px]"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit Idea
                    </button>
                    <button
                      onClick={() => {
                        setDeleteConfirmId(selectedIdeaDetails.id);
                        setSelectedIdeaDetails(null);
                      }}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Idea
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-stone-900">Delete Idea</h3>
            </div>
            
            <p className="text-stone-600 mb-6">
              Are you sure you want to delete this idea? This action cannot be undone.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 px-4 py-3 border border-stone-300 text-stone-600 font-medium rounded-lg hover:bg-stone-50 transition-colors min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeleteIdea(deleteConfirmId);
                  setDeleteConfirmId(null);
                }}
                className="flex-1 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Setup Flow */}
      {projectSetupIdea && (
        <ProjectSetupFlow
          projectIdea={projectSetupIdea}
          onClose={() => setProjectSetupIdea(null)}
          onStartProject={handleProjectSetupComplete}
        />
      )}

      {/* Active Project Workspace */}
      {activeProjectConfig && (
        <ActiveProjectWorkspace
          projectConfig={activeProjectConfig}
          onClose={() => setActiveProjectConfig(null)}
          onSave={handleSaveProject}
        />
      )}
    </div>
  );
}